// Theme-aware synths for the newer gameplay SFX: the laser charge-up, powerup
// pickups, and per-weapon fire sounds. Each dispatches internally on the active
// skin (battle-city / modern / realistic / retro).

import { state } from "../state.js";
import { audioCtx } from "./context.js";
import { globalSfxVolume } from "./sfx.js";
import { playRealisticCannon } from "./realistic.js";

export function playSynthLaserCharge(volume: number) {
  console.log(`[Synth] playSynthLaserCharge volume: ${volume}, state: ${audioCtx.state}, sfxVol: ${globalSfxVolume}`);
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;
  const duration = 1.0; // LASER_DELAY is exactly 1 second

  if (state.modernEnabled) {
    // 1. Rising Sci-Fi sine sweep with high-frequency FM texture
    const osc = audioCtx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(150, now);
    osc.frequency.exponentialRampToValueAtTime(1500, now + duration);

    // FM Modulator osc to add a sci-fi vibration/buzz
    const fmOsc = audioCtx.createOscillator();
    fmOsc.type = "sawtooth";
    fmOsc.frequency.setValueAtTime(45, now);
    
    const fmGain = audioCtx.createGain();
    fmGain.gain.setValueAtTime(30, now);
    fmGain.gain.exponentialRampToValueAtTime(120, now + duration);

    fmOsc.connect(fmGain);
    fmGain.connect(osc.frequency);

    const filter = audioCtx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(300, now);
    filter.frequency.exponentialRampToValueAtTime(3000, now + duration);

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.linearRampToValueAtTime(0.4 * volume * globalSfxVolume, now + duration - 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start(now);
    osc.stop(now + duration);
    fmOsc.start(now);
    fmOsc.stop(now + duration);
    return;
  }

  if (state.battleCityEnabled) {
    // 2. Retro 8-bit pulse arpeggio + frequency sweep with tremolo
    const osc = audioCtx.createOscillator();
    osc.type = "square";
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.linearRampToValueAtTime(1600, now + duration);

    // tremolo LFO (fast pulse volume modulation)
    const lfo = audioCtx.createOscillator();
    lfo.type = "square";
    lfo.frequency.value = 22; // 22Hz tremolo

    const lfoGain = audioCtx.createGain();
    lfoGain.gain.value = 0.5;

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.01 * volume * globalSfxVolume, now);
    gain.gain.linearRampToValueAtTime(0.3 * volume * globalSfxVolume, now + duration - 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);

    osc.connect(gain);
    gain.connect(audioCtx.destination);

    osc.start(now);
    osc.stop(now + duration);
    lfo.start(now);
    lfo.stop(now + duration);
    return;
  }

  if (state.realisticEnabled) {
    // 3. Realistic heavy electrical charging whistle / turbine whine
    const osc = audioCtx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(60, now);
    osc.frequency.exponentialRampToValueAtTime(440, now + duration);

    // Whistling bandpass filter over noise
    const bufferSize = Math.max(100, Math.floor(audioCtx.sampleRate * duration));
    const noiseBuffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      noiseData[i] = Math.random() * 2 - 1;
    }
    const noise = audioCtx.createBufferSource();
    noise.buffer = noiseBuffer;

    const bpFilter = audioCtx.createBiquadFilter();
    bpFilter.type = "bandpass";
    bpFilter.Q.value = 25; // high resonance
    bpFilter.frequency.setValueAtTime(120, now);
    bpFilter.frequency.exponentialRampToValueAtTime(1800, now + duration);

    const oscGain = audioCtx.createGain();
    oscGain.gain.setValueAtTime(0.001, now);
    oscGain.gain.linearRampToValueAtTime(0.35 * volume * globalSfxVolume, now + duration - 0.05);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    const noiseGain = audioCtx.createGain();
    noiseGain.gain.setValueAtTime(0.001, now);
    noiseGain.gain.linearRampToValueAtTime(0.25 * volume * globalSfxVolume, now + duration - 0.05);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    osc.connect(oscGain);
    oscGain.connect(audioCtx.destination);

    noise.connect(bpFilter);
    bpFilter.connect(noiseGain);
    noiseGain.connect(audioCtx.destination);

    osc.start(now);
    osc.stop(now + duration);
    noise.start(now);
    noise.stop(now + duration);
    return;
  }

  // 4. Default Theme: Clean rising sweep
  const osc = audioCtx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(150, now);
  osc.frequency.exponentialRampToValueAtTime(1000, now + duration);

  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.linearRampToValueAtTime(0.4 * volume * globalSfxVolume, now + duration - 0.05);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  osc.connect(gain);
  gain.connect(audioCtx.destination);

  osc.start(now);
  osc.stop(now + duration);
}

export function playSynthPickup(type: string, volume: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;

  if (state.modernEnabled) {
    if (type === "shield") {
      const osc = audioCtx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.15);
      osc.frequency.exponentialRampToValueAtTime(800, now + 0.3);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.3 * volume * globalSfxVolume, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.3);
    } else if (type === "boost") {
      const osc = audioCtx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(200, now);
      osc.frequency.exponentialRampToValueAtTime(800, now + 0.25);

      const filter = audioCtx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(400, now);
      filter.frequency.exponentialRampToValueAtTime(2000, now + 0.25);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.2 * volume * globalSfxVolume, now + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === "scope") {
      const osc = audioCtx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(1800, now);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.3 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.4);
    } else if (type === "heal") {
      [523.25, 659.25, 783.99].forEach((freq, idx) => {
        const osc = audioCtx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, now + idx * 0.05);

        const gain = audioCtx.createGain();
        gain.gain.setValueAtTime(0.001, now + idx * 0.05);
        gain.gain.linearRampToValueAtTime(0.15 * volume * globalSfxVolume, now + idx * 0.05 + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.05 + 0.25);

        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start(now + idx * 0.05);
        osc.stop(now + idx * 0.05 + 0.25);
      });
    } else {
      const osc = audioCtx.createOscillator();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.1);
      osc.frequency.setValueAtTime(1320, now + 0.1);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.25 * volume * globalSfxVolume, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.25);
    }
    return;
  }

  if (state.battleCityEnabled) {
    const osc = audioCtx.createOscillator();
    osc.type = "square";

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.001, now);

    if (type === "shield") {
      osc.frequency.setValueAtTime(300, now);
      osc.frequency.setValueAtTime(600, now + 0.06);
      osc.frequency.setValueAtTime(900, now + 0.12);
      osc.frequency.setValueAtTime(1200, now + 0.18);
      gain.gain.linearRampToValueAtTime(0.2 * volume * globalSfxVolume, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === "boost") {
      osc.frequency.setValueAtTime(150, now);
      osc.frequency.exponentialRampToValueAtTime(1800, now + 0.18);
      gain.gain.linearRampToValueAtTime(0.2 * volume * globalSfxVolume, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.18);
    } else if (type === "scope") {
      osc.frequency.setValueAtTime(1200, now);
      osc.frequency.setValueAtTime(800, now + 0.07);
      gain.gain.linearRampToValueAtTime(0.2 * volume * globalSfxVolume, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.15);
    } else if (type === "heal") {
      osc.frequency.setValueAtTime(523, now);
      osc.frequency.setValueAtTime(659, now + 0.05);
      osc.frequency.setValueAtTime(784, now + 0.1);
      osc.frequency.setValueAtTime(1046, now + 0.15);
      gain.gain.linearRampToValueAtTime(0.2 * volume * globalSfxVolume, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.25);
    } else {
      osc.frequency.setValueAtTime(400, now);
      osc.frequency.setValueAtTime(800, now + 0.06);
      osc.frequency.setValueAtTime(1600, now + 0.12);
      gain.gain.linearRampToValueAtTime(0.2 * volume * globalSfxVolume, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.2);
    }
    return;
  }

  if (state.realisticEnabled) {
    if (type === "shield") {
      const osc = audioCtx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(80, now);
      osc.frequency.linearRampToValueAtTime(40, now + 0.2);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.3 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.2);
    } else if (type === "boost") {
      const osc = audioCtx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(100, now);
      osc.frequency.linearRampToValueAtTime(250, now + 0.3);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.01, now);
      gain.gain.linearRampToValueAtTime(0.25 * volume * globalSfxVolume, now + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.3);
    } else if (type === "scope") {
      const osc = audioCtx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(900, now);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.15 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.08);
    } else if (type === "heal") {
      const bufferSize = audioCtx.sampleRate * 0.25;
      const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      const noise = audioCtx.createBufferSource();
      noise.buffer = buffer;

      const filter = audioCtx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.setValueAtTime(3000, now);
      filter.frequency.exponentialRampToValueAtTime(500, now + 0.25);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.15 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

      noise.connect(filter);
      filter.connect(gain);
      gain.connect(audioCtx.destination);
      noise.start(now);
      noise.stop(now + 0.25);
    } else {
      const osc = audioCtx.createOscillator();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(120, now);
      osc.frequency.setValueAtTime(300, now + 0.08);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.25 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.2);
    }
    return;
  }

  const osc = audioCtx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(type === "heal" ? 523 : 440, now);
  osc.frequency.exponentialRampToValueAtTime(type === "heal" ? 1046 : 880, now + 0.2);

  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.linearRampToValueAtTime(0.3 * volume * globalSfxVolume, now + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + 0.2);
}

export function playSynthWeaponPew(weapon: string, volume: number, rate: number) {
  if (audioCtx.state === "suspended") audioCtx.resume();
  const now = audioCtx.currentTime;

  if (state.modernEnabled) {
    if (weapon === "laser") {
      const osc = audioCtx.createOscillator();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(800 * rate, now);
      osc.frequency.linearRampToValueAtTime(200 * rate, now + 0.2);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.5 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.2);
    } else if (weapon === "sniper") {
      const osc = audioCtx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(1600 * rate, now);
      osc.frequency.exponentialRampToValueAtTime(50 * rate, now + 0.35);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.6 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.35);
    } else if (weapon === "explosive") {
      const osc = audioCtx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(180 * rate, now);
      osc.frequency.exponentialRampToValueAtTime(30 * rate, now + 0.4);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.8 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.4);
    } else if (weapon === "tracking") {
      const osc = audioCtx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(300 * rate, now);
      osc.frequency.linearRampToValueAtTime(900 * rate, now + 0.25);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.35 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.25);
    } else {
      const osc = audioCtx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(900 * rate, now);
      osc.frequency.exponentialRampToValueAtTime(100 * rate, now + 0.12);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.35 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.12);
    }
    return;
  }

  if (state.battleCityEnabled) {
    const osc = audioCtx.createOscillator();
    osc.type = "square";
    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0.2 * volume * globalSfxVolume, now);

    if (weapon === "laser") {
      osc.frequency.setValueAtTime(1200 * rate, now);
      osc.frequency.exponentialRampToValueAtTime(100 * rate, now + 0.25);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    } else if (weapon === "sniper") {
      osc.frequency.setValueAtTime(1800 * rate, now);
      osc.frequency.exponentialRampToValueAtTime(300 * rate, now + 0.2);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    } else if (weapon === "explosive") {
      osc.type = "triangle";
      osc.frequency.setValueAtTime(150 * rate, now);
      osc.frequency.exponentialRampToValueAtTime(20 * rate, now + 0.35);
      gain.gain.setValueAtTime(0.35 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    } else {
      osc.frequency.setValueAtTime(800 * rate, now);
      osc.frequency.exponentialRampToValueAtTime(80 * rate, now + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
    }
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start(now);
    osc.stop(now + 0.35);
    return;
  }

  if (state.realisticEnabled) {
    if (weapon === "laser") {
      const osc = audioCtx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(3000 * rate, now);
      osc.frequency.linearRampToValueAtTime(100 * rate, now + 0.08);

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.4 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.08);
    } else if (weapon === "sniper") {
      const osc = audioCtx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(90 * rate, now);
      osc.frequency.exponentialRampToValueAtTime(30 * rate, now + 0.5);

      const filter = audioCtx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 180;

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.7 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.5);
    } else if (weapon === "explosive") {
      const osc = audioCtx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(60 * rate, now);
      osc.frequency.linearRampToValueAtTime(20 * rate, now + 0.45);

      const filter = audioCtx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 100;

      const gain = audioCtx.createGain();
      gain.gain.setValueAtTime(0.8 * volume * globalSfxVolume, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(now);
      osc.stop(now + 0.45);
    } else {
      playRealisticCannon(volume, rate);
    }
    return;
  }

  const osc = audioCtx.createOscillator();
  osc.type = "sine";
  const gain = audioCtx.createGain();
  gain.gain.setValueAtTime(0.001, now);

  if (weapon === "laser") {
    osc.frequency.setValueAtTime(1500 * rate, now);
    osc.frequency.exponentialRampToValueAtTime(200 * rate, now + 0.15);
    gain.gain.linearRampToValueAtTime(0.35 * volume * globalSfxVolume, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  } else if (weapon === "sniper") {
    osc.frequency.setValueAtTime(2200 * rate, now);
    osc.frequency.exponentialRampToValueAtTime(150 * rate, now + 0.25);
    gain.gain.linearRampToValueAtTime(0.4 * volume * globalSfxVolume, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
  } else if (weapon === "explosive") {
    osc.frequency.setValueAtTime(120 * rate, now);
    osc.frequency.exponentialRampToValueAtTime(40 * rate, now + 0.35);
    gain.gain.linearRampToValueAtTime(0.5 * volume * globalSfxVolume, now + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
  } else {
    osc.frequency.setValueAtTime(700 * rate, now);
    osc.frequency.exponentialRampToValueAtTime(80 * rate, now + 0.15);
    gain.gain.linearRampToValueAtTime(0.3 * volume * globalSfxVolume, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  }
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.start(now);
  osc.stop(now + 0.35);
}
